import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, DollarSign, Plus, TrendingUp } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/utils/trpc";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(amount);
}

function formatFrequency(frequency: string) {
  const map: Record<string, string> = {
    daily: "Diário",
    weekly: "Semanal",
    biweekly: "Quinzenal",
    monthly: "Mensal",
    quarterly: "Trimestral",
    yearly: "Anual",
  };
  return map[frequency] || frequency;
}

export function IncomeSourcesManager() {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<any>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    grossAmount: "",
    netAmount: "",
    recurrenceDay: "",
    frequency: "monthly",
    notes: "",
  });

  // Fetch income sources
  const incomeSources = useQuery(
    trpc.incomeSources.list.queryOptions({ includeInactive: false })
  );

  // Fetch total monthly income
  const totalIncome = useQuery(
    trpc.incomeSources.getTotalMonthlyIncome.queryOptions()
  );

  // Create mutation
  const createMutation = useMutation({
    ...trpc.incomeSources.create.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries();
      setIsDialogOpen(false);
      resetForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    ...trpc.incomeSources.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries();
      setIsDialogOpen(false);
      resetForm();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    ...trpc.incomeSources.delete.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      grossAmount: "",
      netAmount: "",
      recurrenceDay: "",
      frequency: "monthly",
      notes: "",
    });
    setEditingSource(null);
  };

  const handleOpenDialog = (source?: any) => {
    if (source) {
      setEditingSource(source);
      setFormData({
        name: source.name,
        grossAmount: String(source.grossAmount),
        netAmount: String(source.netAmount),
        recurrenceDay: source.recurrenceDay ? String(source.recurrenceDay) : "",
        frequency: source.frequency || "monthly",
        notes: source.notes || "",
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    const payload = {
      name: formData.name,
      grossAmount: formData.grossAmount,
      netAmount: formData.netAmount,
      recurrenceDay: formData.recurrenceDay
        ? Number.parseInt(formData.recurrenceDay)
        : undefined,
      frequency: formData.frequency as any,
      notes: formData.notes || undefined,
    };

    if (editingSource) {
      updateMutation.mutate({
        id: editingSource.id,
        ...payload,
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm("Tem certeza que deseja desativar esta fonte de renda?")) {
      deleteMutation.mutate({ id });
    }
  };

  // Auto-calculate net amount when gross or tax changes
  const handleGrossChange = (value: string) => {
    setFormData((prev) => {
      const gross = Number.parseFloat(value) || 0;
      const net = Number.parseFloat(prev.netAmount) || 0;
      return {
        ...prev,
        grossAmount: value,
        // If net is empty, assume net = gross (no tax)
        netAmount: prev.netAmount === "" ? value : prev.netAmount,
      };
    });
  };

  // Loading state
  if (incomeSources.isLoading || totalIncome.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">Carregando fontes de renda...</div>
      </div>
    );
  }

  // Error state
  if (incomeSources.isError || totalIncome.isError) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span>Erro ao carregar fontes de renda</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const sources = incomeSources.data || [];
  const total = totalIncome.data || { totalGross: 0, totalNet: 0, totalTax: 0 };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Total Net Income */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">Renda Líquida Mensal</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl text-green-600">
              {formatCurrency(total.totalNet)}
            </div>
            <p className="text-muted-foreground text-xs">
              {sources.length} {sources.length === 1 ? "fonte ativa" : "fontes ativas"}
            </p>
          </CardContent>
        </Card>

        {/* Total Gross Income */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">Renda Bruta Mensal</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl text-blue-600">
              {formatCurrency(total.totalGross)}
            </div>
            <p className="text-muted-foreground text-xs">Antes dos impostos</p>
          </CardContent>
        </Card>

        {/* Total Tax */}
        <Card className="border-orange-200 bg-orange-50/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">Impostos Mensais</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl text-orange-600">
              {formatCurrency(total.totalTax)}
            </div>
            <p className="text-muted-foreground text-xs">
              {total.totalGross > 0
                ? `${((total.totalTax / total.totalGross) * 100).toFixed(1)}% da renda bruta`
                : "Nenhum imposto"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Income Sources Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Suas Fontes de Renda</CardTitle>
            <CardDescription>
              Gerencie todas as suas fontes de renda ativas
            </CardDescription>
          </div>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Fonte
          </Button>
        </CardHeader>
        <CardContent>
          {sources.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              Nenhuma fonte de renda cadastrada. Clique em "Nova Fonte" para começar.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Frequência</TableHead>
                  <TableHead className="text-right">Bruto</TableHead>
                  <TableHead className="text-right">Impostos</TableHead>
                  <TableHead className="text-right">Líquido</TableHead>
                  <TableHead>Dia</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.map((source) => (
                  <TableRow key={source.id}>
                    <TableCell className="font-medium">{source.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {formatFrequency(source.frequency)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(Number(source.grossAmount))}
                    </TableCell>
                    <TableCell className="text-right font-mono text-orange-600">
                      -{formatCurrency(Number(source.taxAmount))}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-green-600">
                      {formatCurrency(Number(source.netAmount))}
                    </TableCell>
                    <TableCell>
                      {source.recurrenceDay ? (
                        <Badge variant="secondary">Dia {source.recurrenceDay}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(source)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(source.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Desativar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingSource ? "Editar Fonte de Renda" : "Nova Fonte de Renda"}
            </DialogTitle>
            <DialogDescription>
              {editingSource
                ? "Atualize as informações da fonte de renda"
                : "Adicione uma nova fonte de renda ao seu household"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                placeholder="Ex: Floki, Freelance, Aluguel"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="grossAmount">Valor Bruto (R$)</Label>
                <Input
                  id="grossAmount"
                  type="number"
                  placeholder="0.00"
                  value={formData.grossAmount}
                  onChange={(e) => handleGrossChange(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="netAmount">Valor Líquido (R$)</Label>
                <Input
                  id="netAmount"
                  type="number"
                  placeholder="0.00"
                  value={formData.netAmount}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, netAmount: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="frequency">Frequência</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, frequency: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Diário</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="biweekly">Quinzenal</SelectItem>
                    <SelectItem value="monthly">Mensal</SelectItem>
                    <SelectItem value="quarterly">Trimestral</SelectItem>
                    <SelectItem value="yearly">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="recurrenceDay">Dia do Pagamento</Label>
                <Input
                  id="recurrenceDay"
                  type="number"
                  min="1"
                  max="31"
                  placeholder="1-31"
                  value={formData.recurrenceDay}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      recurrenceDay: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Observações (opcional)</Label>
              <Input
                id="notes"
                placeholder="Ex: Pagamento no último dia útil"
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>

            {/* Tax calculation info */}
            {formData.grossAmount && formData.netAmount && (
              <div className="rounded-md bg-orange-50 p-3 text-sm dark:bg-orange-950/20">
                <p className="font-medium text-orange-900 dark:text-orange-200">
                  💰 Imposto calculado:{" "}
                  {formatCurrency(
                    Number.parseFloat(formData.grossAmount || "0") -
                      Number.parseFloat(formData.netAmount || "0")
                  )}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !formData.name ||
                !formData.grossAmount ||
                !formData.netAmount ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {createMutation.isPending || updateMutation.isPending
                ? "Salvando..."
                : editingSource
                  ? "Atualizar"
                  : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
